import {
  ChapterHighlights,
  HighlightData,
  HighlightRawData,
  HighlightsFilterType,
  HighlightsState,
} from "./types";
import { createSelector } from "@reduxjs/toolkit";
import { getPageBreaks } from "../pagebreaks/selectors";
import {
  findPageLabelFromCfi,
  getDocCorrectedCfi,
  getExtendedCfi,
  safeParseCfiRange,
} from "../../../../../../common/utils/cfi";
import {
  getEmojiMarkers,
  getHighlightsSubscribedMarkers,
  getMarkers,
} from "../markers/selectors";
import { CFI, parseCfi, parseCfiRange } from "epubjs";
import { getCurrentPageCfi, isPageLoading } from "../nav/load-status/selectors";
import { isEmoji, sortChapterHighlights, sortHighlights } from "./utils";
import { getTocNodesWithValidCFIs, getTocTitleCount } from "../toc/selectors";
import { generateUUID } from "../../../../../../common/utils/uuid";
import { getChapterNodeForCFI } from "../toc/utils";
import { getSpineIdrefCfis } from "../spine/selectors";

function getBase(state): HighlightsState {
  return state.highlights;
}

const getMyHighlightsRawData = (state) => getBase(state).highlightsRawData;

export const getSubscribedHighlightsRawData = (state) =>
  getBase(state).subscribedHighlightsRawData;

const getAllHighlightsRawData = createSelector(
  getMyHighlightsRawData,
  getSubscribedHighlightsRawData,
  (myHighlights, subscribeHighlights) =>
    myHighlights.concat(subscribeHighlights)
);

export const getHighlightsLoadStatus = (state) => getBase(state).loadStatus;

export const getReconstructedHighlightsResponse = createSelector(
  (state) => getMyHighlightsRawData(state),
  (state) => getSubscribedHighlightsRawData(state),
  (state) => getMarkers(state),
  (state) => getHighlightsSubscribedMarkers(state),
  (myHighlights, subscribedHighlights, myMarkers, subscribedMarkers) => {
    return {
      highlights: myHighlights,
      subscribedHighlights,
      markers: myMarkers,
      subscribedMarkers,
    };
  }
);

export const getHighlightsData = createSelector(
  getAllHighlightsRawData,
  getPageBreaks,
  getMarkers,
  getEmojiMarkers,
  getHighlightsSubscribedMarkers,
  getSpineIdrefCfis,
  (
    highlightsRawData,
    pageBreaks,
    markers,
    emojiMarkers,
    subscribedMarkers,
    idrefCfis
  ) => {
    const emojiMarkerIds = emojiMarkers.map(
      (emojiMarker) => emojiMarker.globalId
    ); // in case a user has more than one emoji marker
    if (!highlightsRawData || !pageBreaks || !markers || !subscribedMarkers) {
      return undefined;
    }
    const highlightsData = highlightsRawData?.map<HighlightData>(
      (highlightRawData) => ({
        ...highlightRawData,
        startLabel: findStartLabel(highlightRawData, pageBreaks, idrefCfis),
        authorInitials: extractAuthorInitials(highlightRawData.authorName),
        isEmoji:
          isEmoji(highlightRawData) &&
          emojiMarkerIds.includes(highlightRawData.markerId),
        markerColorName:
          markers
            .concat(subscribedMarkers)
            .find((marker) => marker.globalId === highlightRawData.markerId)
            ?.label ?? "",
      })
    );

    return sortHighlights(highlightsData);
  }
);

export const getHighlightsFilter = (state) => getBase(state).highlightsFilter;

export const getFilteredHighlights = createSelector(
  getHighlightsData,
  getHighlightsFilter,
  (highlights, filter) => {
    return highlights?.filter(
      (highlight) =>
        filter.type === HighlightsFilterType.None ||
        (filter.type === HighlightsFilterType.OnlyNotes &&
          highlight.hasNote &&
          highlight.markerColorName) ||
        (filter.type === HighlightsFilterType.Marker &&
          highlight.markerId === filter.globalId)
    );
  }
);

const findStartLabel = (
  highlight: HighlightRawData,
  pageBreaks,
  idrefCfis
): string => {
  let cfiStart: string = "";
  if (highlight.cfi) {
    const range = safeParseCfiRange(highlight.cfi);
    if (!range) {
      return "";
    }
    cfiStart = getDocCorrectedCfi(range.start.toString(), idrefCfis);
  }
  if (!highlight.startLabel && cfiStart) {
    return findPageLabelFromCfi(pageBreaks, cfiStart);
  }
  return highlight.startLabel;
};

const extractAuthorInitials = (
  authorName: string | undefined
): string | undefined => {
  if (!authorName) {
    return undefined;
  }
  return authorName
    .split(" ")
    .reduce(
      (prev, cur, i, parts) =>
        i === 0 || i === parts.length - 1
          ? prev + cur.toUpperCase().charAt(0)
          : prev,
      ""
    );
};

export const getHighlightsForCurrentDocument = createSelector(
  (state) => getHighlightsData(state),
  (state) => getCurrentPageCfi(state),
  (state) => isPageLoading(state),
  (allHighlights, currentCfi, isLoading) => {
    if (!currentCfi || !allHighlights || isLoading) {
      return [];
    }
    const pageCfi = parseCfi(currentCfi);
    return allHighlights.filter((highlight) => {
      let cfiToUse;
      try {
        if (highlight.cfi) {
          cfiToUse = parseCfiRange(highlight.cfi).start.contentDoc;
        } else {
          cfiToUse = parseCfi(`/${highlight.startPage}`);
        }
        return CFI.equals(pageCfi, cfiToUse, { checkAssertions: true });
      } catch {
        return false;
      }
    });
  }
);

export const getHighlightForId = (highlightId) =>
  createSelector(getHighlightsData, (highlights) => {
    if (highlights) {
      if (!highlightId) {
        return null;
      }
      return highlights.find((h) => h.syncId === highlightId);
    } else {
      return null;
    }
  });

export const getHighlightMarker = (highlightId) =>
  createSelector(
    getMarkers,
    getHighlightForId(highlightId),
    (markers, highlight) => {
      if (!highlight) {
        return null;
      }

      return markers.find((m) => m.globalId === highlight.markerId);
    }
  );

export const getFilteredHighlightsByChapter = createSelector(
  getFilteredHighlights,
  getTocTitleCount,
  getTocNodesWithValidCFIs,
  getSpineIdrefCfis,
  (filteredHighlights, chapterTitleCount, tocNodesWithValidCFIS, idrefCfis) => {
    const filtered = filteredHighlights?.reduce<ChapterHighlights>(
      (result, highlight, i, values) => {
        let uuid;
        let groupCfi;
        let chapterTitle = highlight.chapterTitle;
        const lastGroup = result[result.length - 1];
        const prevUuid = lastGroup?.uuid;
        const safeRange = safeParseCfiRange(highlight.cfi);

        if (safeRange) {
          const lookupCfi = getDocCorrectedCfi(
            safeRange.start.toString(),
            idrefCfis
          );
          const node = getChapterNodeForCFI(lookupCfi, {
            toc: tocNodesWithValidCFIS,
          });
          if (node) {
            uuid = node.uuid;
            chapterTitle = node.title;
            groupCfi = node.cfi;
          }
        }

        // Check if node uuid is the same as last checked uuid
        // Fall back to chapter title match if CFI could not be parsed
        if (
          lastGroup &&
          ((uuid && uuid === prevUuid) ||
            (chapterTitle && chapterTitle === lastGroup.title))
        ) {
          lastGroup.highlights.push(highlight);
        } else {
          result.push({
            cfi: getExtendedCfi(groupCfi),
            title: chapterTitle,
            uuid: uuid || `${chapterTitle}_${generateUUID()}`,
            highlights: [highlight],
          });
        }
        return result;
      },
      []
    );

    return sortChapterHighlights(filtered);
  }
);
